import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getMyMatches, getPlaces, addSailingopp, editopp, deactivateopp, matchAnswer, createMatch, getOpportunitiesByDomain } from '../api/api';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel, Stack, Tooltip, IconButton, Collapse, Snackbar, Alert, CircularProgress, Paper, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CancelIcon from '@mui/icons-material/Cancel';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Typography, Box, List } from '@mui/material';
import { getInitials } from '../utils';
import MatchListItem from './MatchListItem';

const MatchListCrew = ({ token, domain, homeclub, showInPublicRoster }) => {
    const { t } = useTranslation();
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [places, setPlaces] = useState([]);
    const [showInterested, setShowInterested] = useState(false);
    const [showOther, setShowOther] = useState(false);
    const [showNotInterested, setShowNotInterested] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [consentDialogOpen, setConsentDialogOpen] = useState(false);
    const [pendingMatchId, setPendingMatchId] = useState(null);
    const [pendingOppId, setPendingOppId] = useState(null);
    const [hasConsented, setHasConsented] = useState(false);
    const [areaOpportunities, setAreaOpportunities] = useState([]);

    useEffect(() => {
        const consent = localStorage.getItem('contactInfoConsent');
        setHasConsented(consent === 'true');
    }, []);

    const placeIdToName = (id) => {
        const name = places?.find(x => x._id == id)?.name;
        return name ?? "Unknown location"
    }



    const loadMatches = useCallback(async () => {
        try {
            setLoading(true);
            
            // Load matches
            const matchesData = await getMyMatches(token);
            setMatches(matchesData.results);
            
            // Load domain opportunities 
            if (domain) {
                try {
                    // Get opportunities list in the area
                    const opportunitiesData = await getOpportunitiesByDomain(token, domain);
                    setAreaOpportunities(opportunitiesData);
                } catch (error) {
                    console.error('Error loading area opportunities:', error);
                }
            }
        } catch (error) {
            console.error('Error loading matches:', error);
        } finally {
            setLoading(false);
        }
    }, [token, domain]);

    const loadPlaces = useCallback(async () => {
        try {
            const data = await getPlaces(domain);
            setPlaces(data);
        } catch (error) {
            console.error('Error loading matches:', error);
        }
    }, [token, domain]);

    useEffect(() => {
        loadMatches();
    }, [loadMatches]);

    useEffect(() => {
        loadPlaces();
    }, [loadPlaces]);

    // Auto-expand Other Opportunities section when there are no new matches but there are other opportunities
    useEffect(() => {
        if (matches.filter(m => !m.match.response).length === 0 && areaOpportunities.length > 0) {
            setShowOther(true);
        }
    }, [matches, areaOpportunities]);

    const handleInterestedClick = async (matchId, answer) => {        
        if (answer === 1 && !hasConsented) {
            setPendingMatchId(matchId);
            setConsentDialogOpen(true);
            return;
        }

        try {
            const m = matches.filter(x => x.match._id == matchId)[0];
            const updatedMatch = await matchAnswer(token, m.opp._id, answer);

            const newMatches = matches.map((item) => (item.match._id == matchId)
                ? { ...item, match: updatedMatch }
                : item)
            setMatches(newMatches);

            if (answer === 1) {
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Error updating match response:', error);
            setErrorMessage(t('match.errorUpdatingResponse'));
            setErrorSnackbarOpen(true);
        }
    };


    const createNewMatch = async (oppId, answer) => {
        try {
            // Create the match
            await createMatch(token, oppId, answer);
            // Reload all matches from the server instead of manually updating the array
            await loadMatches();

            if (answer === 1) {
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Error creating match:', error);
            setErrorMessage(t('match.errorCreatingMatch'));
            setErrorSnackbarOpen(true);
        }
    }
     
    const handleOtherOppInterestClick = async (oppId, answer) => {
        if (answer === 1 && !hasConsented) {
            setPendingOppId(oppId);
            setConsentDialogOpen(true);
            return;
        }
      
        await createNewMatch(oppId, answer);
    };


    const handleConsent = async () => {
        try {
            localStorage.setItem('contactInfoConsent', 'true');
            setHasConsented(true);
            setConsentDialogOpen(false);
            
            if (pendingMatchId) {
                const m = matches.filter(x => x.match._id == pendingMatchId)[0];
                const updatedMatch = await matchAnswer(token, m.opp._id, 1);
        
                const newMatches = matches.map((item) => (item.match._id == pendingMatchId)
                    ? { ...item, match: updatedMatch }
                    : item)
                setMatches(newMatches);
                setSnackbarOpen(true);
                setPendingMatchId(null);
            }

            if (pendingOppId) {
                await createNewMatch(pendingOppId, 1);
                setPendingOppId(null);
            }
        } catch (error) {
            console.error('Error handling consent:', error);
            setErrorMessage(t('match.errorProcessingRequest'));
            setErrorSnackbarOpen(true);
        }
    };

    const handleConsentDecline = () => {
        setConsentDialogOpen(false);
        setPendingMatchId(null);
        setPendingOppId(null);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const handleErrorSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setErrorSnackbarOpen(false);
    };

    return (
        <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
            {/* Invitations Section */}
            {matches.filter(m => m.match.type === 'invitation' && !m.match.response).length > 0 && (
                <Box sx={{ marginBottom: 2 }}>
                    <Typography variant="h6" color="primary" sx={{ marginBottom: 1 }}>
                        {t('match.invitations')} ({matches.filter(m => m.match.type === 'invitation' && !m.match.response).length})
                    </Typography>
                    <List sx={{ width: '100%' }}>
                        {matches
                            .filter(m => m.match.type === 'invitation' && !m.match.response)
                            .map((match) => (
                                <MatchListItem
                                    key={match.match._id}
                                    match={match.match}
                                    skipper={match.skipper}
                                    opp={match.opp}
                                    onInterestClick={handleInterestedClick}
                                    placeIdToName={placeIdToName}
                                    isInvitation={true}
                                />
                            ))}
                    </List>
                </Box>
            )}

            {/* New Opportunities Section */}

            { matches.filter(m => !m.match.response && m.match.type !== 'invitation').length === 0  ?
            (
                <>
                <Typography variant="h6" color="textSecondary" sx={{ marginBottom: 0 }}>
                    {t('match.noNewMatches')}
                </Typography>
                {!showInPublicRoster && (
                    <Typography variant="body1" color="textSecondary" sx={{ marginTop: 1, marginBottom: 1 }}>
                        <Chip label={t('match.new')} color="primary" size="small" sx={{ marginRight: 1, fontWeight: 'bold' }} />
                        {t('match.joinRosterSuggestion')}{' '}
                        <Link to="/profile?step=aboutYou">{t('match.joinRosterLink')}</Link>
                    </Typography>
                )}
                </>
            ) :
                <Typography variant="h6" color="textSecondary" sx={{ marginBottom: 0 }}>
                    {t('match.newMatches')}
                </Typography>
            }
            {matches.length === 0 ? (
                <Typography variant="body1" color="textSecondary" sx={{ marginBottom: 1 }}>
                    {t('match.noMatches')}
                </Typography>
            ) : matches.filter(m => !m.match.response && m.match.type !== 'invitation').length === 0 ? (
                <Typography variant="body1" color="textSecondary" sx={{ marginBottom: 1 }}>
                    {t('match.allResponded')}
                </Typography>
            ) : (
                <>
                <List sx={{ width: '100%' }}>
                    {matches
                        .filter(m => !m.match.response && m.match.type !== 'invitation')
                        .map((match) => (
                            <MatchListItem
                                key={match.match._id}
                                match={match.match}
                                skipper={match.skipper}
                                opp={match.opp}
                                onInterestClick={handleInterestedClick}
                                placeIdToName={placeIdToName}
                                isInvitation={match.match.type === 'invitation'}
                            />
                        ))}
                </List>
                </>
            )}
             {/* Club Opportunities Section */}
             
             {domain && areaOpportunities.length == 0 &&
                        <Typography variant="body1"  color="textSecondary">
                            {t('match.noAreaOpportunities')}
                        </Typography>
             }


            {/* Other Opportunities Section */}
             {areaOpportunities.length > 0 && (
                <Box sx={{ marginTop: 1 }}>
                    <Stack 
                        direction="row" 
                        alignItems="center" 
                        spacing={1}
                        sx={{ marginBottom: 1, cursor: 'pointer' }}
                        onClick={() => setShowOther(!showOther)}
                    >
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            {t('match.otherOpportunities')} ({areaOpportunities.length})
                        </Typography>
                        <IconButton size="small">
                            {showOther ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                    <Collapse in={showOther}>
                        <List sx={{ width: '100%' }}>
                            {areaOpportunities
                                .map((opp) => (
                                    <MatchListItem
                                        key={opp._id}
                                        match={opp}
                                        skipper={opp.skipper}
                                        opp={opp}
                                        onInterestClick={handleOtherOppInterestClick}
                                        placeIdToName={placeIdToName}
                                    />
                                ))}
                        </List>
                    </Collapse>
                </Box>
            )}



            {/* Interested Opportunities Section */}
            {matches.filter(m => m.match.response === 1).length > 0 && (
                <Box sx={{ marginTop: 1 }}>
                    <Stack 
                        direction="row" 
                        alignItems="center" 
                        spacing={1}
                        sx={{ marginBottom: 1, cursor: 'pointer' }}
                        onClick={() => setShowInterested(!showInterested)}
                    >
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            {t('match.interested')} ({matches.filter(m => m.match.response === 1).length})
                        </Typography>
                        <IconButton size="small">
                            {showInterested ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                    <Collapse in={showInterested}>
                        <List sx={{ width: '100%' }}>
                            {matches
                                .filter(m => m.match.response === 1)
                                .map((match) => (
                                    <MatchListItem
                                        key={match.match._id}
                                        match={match.match}
                                        skipper={match.skipper}
                                        opp={match.opp}
                                        onInterestClick={handleInterestedClick}
                                        placeIdToName={placeIdToName}
                                    />
                                ))}
                        </List>
                    </Collapse>
                </Box>
            )}

            {/* Not Interested Opportunities Section */}
            {matches.filter(m => m.match.response === 2).length > 0 && (
                <Box sx={{ marginTop: 1}}>
                    <Stack 
                        direction="row" 
                        alignItems="center" 
                        spacing={1}
                        sx={{ marginBottom: 1, cursor: 'pointer' }}
                        onClick={() => setShowNotInterested(!showNotInterested)}
                    >
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            {t('match.notInterestedPlural')} ({matches.filter(m => m.match.response === 2).length})
                        </Typography>
                        <IconButton size="small">
                            {showNotInterested ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                    <Collapse in={showNotInterested}>
                        <List sx={{ width: '100%' }}>
                            {matches
                                .filter(m => m.match.response === 2)
                                .map((match) => (
                                    <MatchListItem
                                        key={match.match._id}
                                        match={match.match}
                                        skipper={match.skipper}
                                        opp={match.opp}
                                        onInterestClick={handleInterestedClick}
                                        placeIdToName={placeIdToName}
                                    />
                                ))}
                        </List>
                    </Collapse>
                </Box>
            )}

           

            <Snackbar 
                open={snackbarOpen} 
                autoHideDuration={5000} 
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity="info" sx={{ width: '100%' }}>
                    {t('match.notification')}
                </Alert>
            </Snackbar>
            <Snackbar 
                open={errorSnackbarOpen} 
                autoHideDuration={5000} 
                onClose={handleErrorSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleErrorSnackbarClose} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
            <Dialog open={consentDialogOpen} onClose={handleConsentDecline}>
                <DialogTitle>{t('match.shareInfo.title')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('match.shareInfo.message')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConsentDecline} color="error">
                        {t('match.shareInfo.cancel')}
                    </Button>
                    <Button onClick={handleConsent} color="primary" variant="contained">
                        {t('match.shareInfo.agree')}
                    </Button>
                </DialogActions>
            </Dialog>
                </>
            )}
        </Box>
    );
};

export default MatchListCrew;
